/**
 * CourtyardContext - "From the Courtyard" daily tip state for Finding Your Voice
 *
 * Serves the tip of the day (day-keyed rotation through data/tips.js with a
 * viewed-tips memory so recent tips don't repeat) and the chat panel
 * open/close state for future AI support. Equivalent to PembertonContext
 * in Pocket Butler.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { CHARACTERS as characters } from '../data/characters';
import { TIPS } from '../data/tips';

const CourtyardContext = createContext(null);

const STORAGE_KEY = 'fyvCourtyardState';

export const CourtyardProvider = ({ children }) => {
  const [tipOfTheDay, setTipOfTheDay] = useState(null);
  const [lastTipDate, setLastTipDate] = useState(null);
  const [viewedTips, setViewedTips] = useState([]);
  const [favouriteQuotes, setFavouriteQuotes] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);

  // Load state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setLastTipDate(parsed.lastTipDate || null);
        setViewedTips(parsed.viewedTips || []);
        setFavouriteQuotes(parsed.favouriteQuotes || []);
      }
    } catch (err) {
      console.error('Failed to load courtyard state:', err);
    }
  }, []);

  // Save state to localStorage
  const saveState = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        lastTipDate,
        viewedTips,
        favouriteQuotes,
      }));
    } catch (err) {
      console.error('Failed to save courtyard state:', err);
    }
  }, [lastTipDate, viewedTips, favouriteQuotes]);

  // Auto-save when state changes
  useEffect(() => {
    const timeoutId = setTimeout(saveState, 500);
    return () => clearTimeout(timeoutId);
  }, [saveState]);

  /**
   * The tips library, with character names resolved for display
   */
  const allTips = useMemo(() =>
    TIPS.map((tip) => ({
      ...tip,
      characterName:
        characters?.find((c) => c.id === tip.characterId)?.name || 'Your Guide',
    })),
  []);

  /**
   * Get today's tip (daily rotating)
   */
  const getTipOfTheDay = useCallback(() => {
    if (allTips.length === 0) return null;

    const today = new Date().toDateString();

    if (lastTipDate === today && tipOfTheDay) {
      return tipOfTheDay;
    }

    // Get a tip that hasn't been viewed recently
    const availableTips = allTips.filter(tip => !viewedTips.includes(tip.id));
    const tipsToChooseFrom = availableTips.length > 0 ? availableTips : allTips;

    // Use date as seed for consistent daily tip
    const dateNum = new Date().getDate() + new Date().getMonth() * 31;
    const tipIndex = dateNum % tipsToChooseFrom.length;
    const newTip = tipsToChooseFrom[tipIndex];

    setTipOfTheDay(newTip);
    setLastTipDate(today);
    setViewedTips(prev => {
      const newViewed = [...prev, newTip.id];
      // Keep only last 30 viewed tips
      return newViewed.slice(-30);
    });

    return newTip;
  }, [allTips, lastTipDate, tipOfTheDay, viewedTips]);

  /**
   * Toggle the chat panel open/closed
   */
  const toggleChatOpen = useCallback(() => {
    setChatOpen(prev => !prev);
  }, []);

  const value = useMemo(() => ({
    // State
    tipOfTheDay,
    viewedTips,
    favouriteQuotes,
    chatOpen,

    // Methods
    getTipOfTheDay,
    toggleChatOpen,
  }), [
    tipOfTheDay,
    viewedTips,
    favouriteQuotes,
    chatOpen,
    getTipOfTheDay,
    toggleChatOpen,
  ]);

  return (
    <CourtyardContext.Provider value={value}>
      {children}
    </CourtyardContext.Provider>
  );
};

export const useCourtyardChat = () => {
  const context = useContext(CourtyardContext);
  if (!context) {
    throw new Error('useCourtyardChat must be used within a CourtyardProvider');
  }
  return context;
};

export default CourtyardContext;
